// src/utils/alertApi.js
const BASE_URL = process.env.REACT_APP_BACKEND_URL + "/api/alerts";

// Helper to get Auth Header (same as in deviceApi.js)
const getAuthHeader = () => {
    const token = localStorage.getItem('piwatch_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Helper to handle 401 Unauthorized (same as in deviceApi.js)
const handleUnauthorized = (response) => {
    if (response.status === 401) {
        console.error("⛔ Unauthorized (401): Token invalid/expired. Logging out.");
        localStorage.removeItem('piwatch_token');
        window.location.reload();
        return true;
    }
    return false;
};

/**
 * Fetches all alert rules for the logged-in user.
 */
export const fetchAlertRules = async () => {
    try {
        const headers = { ...getAuthHeader() };
        const res = await fetch(`${BASE_URL}/rules`, { headers });
        if (handleUnauthorized(res)) return [];
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    } catch (err) {
        console.error("❌ Fetch alert rules error:", err);
        return [];
    }
};

/**
 * Adds a new alert rule.
 * ruleData should contain: device_id, metric, condition, threshold OR status_threshold, name
 */
export const addAlertRule = async (ruleData) => {
    try {
        const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
        const res = await fetch(`${BASE_URL}/rules`, {
            method: "POST",
            headers: headers,
            body: JSON.stringify(ruleData),
        });
        if (handleUnauthorized(res)) return null;
        const result = await res.json();
        if (!res.ok) {
            console.error("Add rule failed:", result);
            throw new Error(result.error || `HTTP error ${res.status}`);
        }
        return result; // Contains { message, rule_id, rules }
    } catch (err) {
        console.error("❌ Add alert rule error:", err);
        alert(`Error adding rule: ${err.message}`);
        return null;
    }
};

/**
 * Deletes an alert rule by its ID.
 */
export const deleteAlertRule = async (ruleId) => {
    try {
        const headers = { ...getAuthHeader() };
        const res = await fetch(`${BASE_URL}/rules/${ruleId}`, {
            method: "DELETE",
            headers: headers,
        });
        if (handleUnauthorized(res)) return null;
        const result = await res.json();
        if (!res.ok) {
             console.error("Delete rule failed:", result);
            throw new Error(result.error || `HTTP error ${res.status}`);
        }
        return result; // Contains { message, rules }
    } catch (err) {
        console.error("❌ Delete alert rule error:", err);
        alert(`Error deleting rule: ${err.message}`);
        return null;
    }
};

/**
 * Fetches triggered alerts for the logged-in user.
 * Optional: filter by acknowledged status (true/false/null)
 */
export const fetchTriggeredAlerts = async (acknowledged = null, deviceId = null) => {
    try {
        const headers = { ...getAuthHeader() };
        const params = new URLSearchParams();

        if (acknowledged !== null) {
            params.append('acknowledged', acknowledged ? '1' : '0');
        }
        
        // ✅ CORRECTED FILTER LOGIC: Send the device ID only if it's NOT 'ALL' 
        // OR send nothing, as the backend defaults to ALL when no filter is present.
        if (deviceId && deviceId !== 'ALL') {
             params.append('device_id', deviceId);
        }

        // Construct the URL with parameters
        let url = `${BASE_URL}/triggered`;
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        // Console log the URL to confirm it's correct for debugging
        console.log("Fetching triggered alerts from URL:", url); 

        const res = await fetch(url, { headers, cache: 'no-store' }); // Disable cache for reliability
        
        if (handleUnauthorized(res)) return [];
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    } catch (err) {
        console.error("❌ Fetch triggered alerts error:", err);
        return [];
    }
};

/**
 * Acknowledges a specific triggered alert.
 */
export const acknowledgeTriggeredAlert = async (alertId) => {
    try {
        const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
        const res = await fetch(`${BASE_URL}/triggered/${alertId}/ack`, {
            method: "POST",
            headers: headers,
        });
        if (handleUnauthorized(res)) return null;
        const result = await res.json();
        if (!res.ok) {
            console.error("Acknowledge alert failed:", result);
            throw new Error(result.error || `HTTP error ${res.status}`);
        }
        return result; // Contains { message, unacknowledged_alerts }
    } catch (err) {
        console.error("❌ Acknowledge alert error:", err);
        alert(`Error acknowledging alert: ${err.message}`);
        return null;
    }
};
// src/utils/alertApi.js
// ... (other functions) ...

export const acknowledgeAllTriggeredAlerts = async () => {
    try {
        const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
        const res = await fetch(`${BASE_URL}/triggered/ack-all`, {
            method: "POST",
            headers: headers,
        });
        if (handleUnauthorized(res)) return null;
        const result = await res.json();
        if (!res.ok) {
            console.error("Acknowledge all alerts failed:", result);
            throw new Error(result.error || `HTTP error ${res.status}`);
        }
        return result; // Contains { message, unacknowledged_alerts }
    } catch (err) {
        console.error("❌ Acknowledge all alerts error:", err);
        alert(`Error acknowledging all alerts: ${err.message}`);
        return null;
    }
};
export const toggleAlertRule = async (ruleId, newStatus) => {
    try {
        const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
        const res = await fetch(`${BASE_URL}/rules/${ruleId}/toggle`, {
            method: "POST",
            headers: headers,
            body: JSON.stringify({ enabled: newStatus }), // Send boolean status
        });
        if (handleUnauthorized(res)) return null;
        const result = await res.json();
        if (!res.ok) {
            console.error("Toggle rule failed:", result);
            throw new Error(result.error || `HTTP error ${res.status}`);
        }
        return result; // Should return { message, enabled: newStatus }
    } catch (err) {
        console.error("❌ Toggle alert rule error:", err);
        alert(`Error toggling rule: ${err.message}`);
        return null;
    }
};